import {USER_KEYS} from "@/api/userApi";
import LanguageSelector from "@/components/Common/LanguageSelector";
import ThemeSelector from "@/components/Common/ThemeSelector";
import NotificationPreferenceRow from "@/components/Settings/NotificationPreferenceRow";
import SkeletonLoader from "@/components/onboarding/SkeletonLoader";
import type {NotificationPreferenceListSerializer} from "@/generated/DjangoTypes";
import {useGenericInfiniteQuery} from "@/hooks/useQueryHooks";
import {useUserId} from "@/hooks/useUserHooks";
import {useEffect, type FC} from "react";
import {useTranslation} from "react-i18next";

const AccountPreferencesPane: FC = () => {
  const {t} = useTranslation(["common", "settings"]);
  const userId = useUserId();

  const notificationPreferencesQuery = useGenericInfiniteQuery<NotificationPreferenceListSerializer>({
    queryKey: USER_KEYS.qUserNotificationPreferences(userId),
    enabled: !!userId,
  });

  useEffect(() => {
    if (notificationPreferencesQuery.hasNextPage && !notificationPreferencesQuery.isFetchingNextPage)
      notificationPreferencesQuery.fetchNextPage();
  }, [
    notificationPreferencesQuery.data,
    notificationPreferencesQuery.hasNextPage,
    notificationPreferencesQuery.isFetchingNextPage,
  ]);

  return (
    <div className="flex flex-col gap-8">
      <span className="text-neutral-black text-lg leading-none font-bold">{t("common:Preferences")}</span>
      <div className="flex flex-col gap-4">
        <div className="sm flex items-center justify-between gap-3 max-lg:sm:flex-col max-lg:sm:items-start">
          <div className="flex min-w-0 flex-col gap-1">
            <span className="text-neutral-black text-sm font-medium">{t("settings:appearance")}</span>
            <span className="text-neutral-grey-dark text-xs">{t("settings:appearance_description")}</span>
          </div>
          <div className="shrink-0">
            <ThemeSelector variant="segmented" />
          </div>
        </div>
        <div className="border-neutral-grey-light-dark border-t" />
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            <span className="text-neutral-black text-sm font-medium">{t("settings:language")}</span>
          </div>
          <div className="shrink-0">
            <LanguageSelector variant="dropdown" />
          </div>
        </div>
        <div className="border-neutral-grey-light-dark border-t" />
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-neutral-black text-sm font-medium">{t("settings:email_notifications")}</span>
            <span className="text-neutral-grey-dark text-xs">
              {t("settings:email_notifications_description", {count: notificationPreferencesQuery.data?.length ?? 0})}
            </span>
          </div>
          {notificationPreferencesQuery.isPending ? (
            <div className="flex items-center justify-between gap-6">
              <div className="flex min-w-0 grow items-center gap-3">
                <SkeletonLoader className="size-8 shrink-0 rounded-full" />
                <SkeletonLoader className="h-4 max-w-40" />
              </div>
              <SkeletonLoader className="h-6 w-12 shrink-0" />
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {notificationPreferencesQuery.data?.map(preference => (
                <NotificationPreferenceRow key={preference.id} preference={preference} userId={userId} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountPreferencesPane;
